/**
 * Draft Version History - View and Restore Previous Versions
 *
 * FERP CRITICAL: Production-ready version history management
 *
 * Backend: DraftRepository (Redis-backed)
 * Storage: Version history with timestamps
 *
 * Features:
 * - Version timeline visualization
 * - Version comparison (current vs selected)
 * - Diff highlighting
 * - Version restore
 * - Version metadata display
 * - Change descriptions
 *
 * Revenue Impact: Part of $200K/year Draft Management
 */

import React, { useState } from 'react';
import {
  Stack,
  Paper,
  Title,
  Text,
  Button,
  Group,
  Timeline,
  Badge,
  Code,
  Alert,
  Tabs,
  JsonInput,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconHistory,
  IconRestore,
  IconCheck,
  IconX,
  IconInfoCircle,
  IconArrowBackUp,
  IconGitCompare,
} from '@tabler/icons-react';
import { DraftVersion } from '../hooks/useDraftWorkflow';

interface DraftVersionHistoryProps {
  versions: DraftVersion[];
  currentData: Record<string, any>;
  onRestore: (version: number) => Promise<void>;
  onClose: () => void;
}

/**
 * Draft Version History Component
 */
export function DraftVersionHistory({
  versions,
  currentData,
  onRestore,
  onClose,
}: DraftVersionHistoryProps) {
  const [selectedVersion, setSelectedVersion] = useState<DraftVersion | null>(null);
  const [compareMode, setCompareMode] = useState(false);

  // Calculate diff between two versions
  const calculateDiff = (
    oldData: Record<string, any>,
    newData: Record<string, any>
  ): {
    added: string[];
    removed: string[];
    modified: string[];
  } => {
    const diff = {
      added: [] as string[],
      removed: [] as string[],
      modified: [] as string[],
    };

    const oldKeys = new Set(Object.keys(oldData));
    const newKeys = new Set(Object.keys(newData));

    // Find added keys
    for (const key of newKeys) {
      if (!oldKeys.has(key)) {
        diff.added.push(key);
      }
    }

    // Find removed keys
    for (const key of oldKeys) {
      if (!newKeys.has(key)) {
        diff.removed.push(key);
      }
    }

    // Find modified keys
    for (const key of oldKeys) {
      if (newKeys.has(key) && JSON.stringify(oldData[key]) !== JSON.stringify(newData[key])) {
        diff.modified.push(key);
      }
    }

    return diff;
  };

  // Get diff between selected version and current
  const getDiff = () => {
    if (!selectedVersion) return null;
    return calculateDiff(selectedVersion.data, currentData);
  };

  // Handle restore version
  const handleRestore = async (version: number) => {
    if (confirm(`Are you sure you want to restore version ${version}? This will create a new version.`)) {
      await onRestore(version);
    }
  };

  const diff = getDiff();

  return (
    <Stack gap="lg">
      {/* Version Timeline */}
      {!compareMode ? (
        <Paper shadow="xs" p="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={4}>Version History</Title>
              {selectedVersion && (
                <Button
                  variant="outline"
                  leftSection={<IconGitCompare size={16} />}
                  onClick={() => setCompareMode(true)}
                >
                  Compare with Current
                </Button>
              )}
            </Group>

            <Alert icon={<IconInfoCircle size={16} />} color="blue">
              <Text size="xs">
                Select a version to view details. Click &quot;Restore&quot; to revert to that version (creates a new
                version).
              </Text>
            </Alert>

            <Timeline active={-1} lineWidth={2}>
              {[...versions].reverse().map((version, idx) => (
                <Timeline.Item
                  key={version.version}
                  bullet={<IconHistory size={12} />}
                  title={
                    <Group gap="xs">
                      <Badge size="sm">Version {version.version}</Badge>
                      {idx === 0 && (
                        <Badge color="green" size="sm">
                          Current
                        </Badge>
                      )}
                    </Group>
                  }
                >
                  <Stack gap="xs">
                    <Text size="xs" c="dimmed">
                      Saved by {version.savedBy}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {new Date(version.savedAt).toLocaleString()}
                    </Text>

                    {version.changeDescription && (
                      <Text size="xs" fs="italic">
                        &quot;{version.changeDescription}&quot;
                      </Text>
                    )}

                    <Group gap="xs" mt="xs">
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => setSelectedVersion(version)}
                      >
                        View Data
                      </Button>
                      {idx > 0 && (
                        <Button
                          size="xs"
                          variant="outline"
                          color="orange"
                          leftSection={<IconRestore size={12} />}
                          onClick={() => handleRestore(version.version)}
                        >
                          Restore
                        </Button>
                      )}
                    </Group>
                  </Stack>
                </Timeline.Item>
              ))}
            </Timeline>

            {versions.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                No version history available
              </Text>
            )}
          </Stack>
        </Paper>
      ) : (
        /* Compare Mode */
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={4}>Compare Versions</Title>
            <Button variant="subtle" onClick={() => setCompareMode(false)}>
              Back to Timeline
            </Button>
          </Group>

          {selectedVersion && diff && (
            <>
              {/* Diff Summary */}
              <Paper shadow="xs" p="md">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text fw={500}>
                      Comparing Version {selectedVersion.version} with Current
                    </Text>
                    <Group gap="xs">
                      <Badge color="green">{diff.added.length} Added</Badge>
                      <Badge color="orange">{diff.modified.length} Modified</Badge>
                      <Badge color="red">{diff.removed.length} Removed</Badge>
                    </Group>
                  </Group>

                  {/* Change Details */}
                  {diff.added.length > 0 && (
                    <div>
                      <Text size="sm" fw={500} c="green" mb="xs">
                        Added Fields:
                      </Text>
                      <Group gap="xs">
                        {diff.added.map((key) => (
                          <Badge key={key} color="green" size="sm">
                            {key}
                          </Badge>
                        ))}
                      </Group>
                    </div>
                  )}

                  {diff.modified.length > 0 && (
                    <div>
                      <Text size="sm" fw={500} c="orange" mb="xs">
                        Modified Fields:
                      </Text>
                      <Group gap="xs">
                        {diff.modified.map((key) => (
                          <Badge key={key} color="orange" size="sm">
                            {key}
                          </Badge>
                        ))}
                      </Group>
                    </div>
                  )}

                  {diff.removed.length > 0 && (
                    <div>
                      <Text size="sm" fw={500} c="red" mb="xs">
                        Removed Fields:
                      </Text>
                      <Group gap="xs">
                        {diff.removed.map((key) => (
                          <Badge key={key} color="red" size="sm">
                            {key}
                          </Badge>
                        ))}
                      </Group>
                    </div>
                  )}
                </Stack>
              </Paper>

              {/* Side-by-Side Comparison */}
              <Tabs defaultValue="sidebyside">
                <Tabs.List>
                  <Tabs.Tab value="sidebyside">Side by Side</Tabs.Tab>
                  <Tabs.Tab value="unified">Unified View</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="sidebyside" pt="md">
                  <Group grow ta="left">
                    <Paper withBorder p="md">
                      <Stack gap="xs">
                        <Group justify="space-between">
                          <Text size="sm" fw={500}>
                            Version {selectedVersion.version}
                          </Text>
                          <Badge size="sm">Old</Badge>
                        </Group>
                        <JsonInput
                          value={JSON.stringify(selectedVersion.data, null, 2)}
                          readOnly
                          minRows={20}
                          maxRows={30}
                          autosize
                        />
                      </Stack>
                    </Paper>

                    <Paper withBorder p="md">
                      <Stack gap="xs">
                        <Group justify="space-between">
                          <Text size="sm" fw={500}>
                            Current Version
                          </Text>
                          <Badge color="green" size="sm">
                            New
                          </Badge>
                        </Group>
                        <JsonInput
                          value={JSON.stringify(currentData, null, 2)}
                          readOnly
                          minRows={20}
                          maxRows={30}
                          autosize
                        />
                      </Stack>
                    </Paper>
                  </Group>
                </Tabs.Panel>

                <Tabs.Panel value="unified" pt="md">
                  <Paper withBorder p="md">
                    <Stack gap="md">
                      {/* Show changed fields */}
                      {[...diff.added, ...diff.modified, ...diff.removed].map((key) => (
                        <div key={key}>
                          <Group gap="xs" mb="xs">
                            <Code>{key}</Code>
                            {diff.added.includes(key) && (
                              <Badge color="green" size="xs">
                                Added
                              </Badge>
                            )}
                            {diff.modified.includes(key) && (
                              <Badge color="orange" size="xs">
                                Modified
                              </Badge>
                            )}
                            {diff.removed.includes(key) && (
                              <Badge color="red" size="xs">
                                Removed
                              </Badge>
                            )}
                          </Group>

                          {diff.removed.includes(key) && (
                            <Paper withBorder p="xs" style={{ backgroundColor: '#ffe0e0' }}>
                              <Text size="xs" c="red" fw={500}>
                                - {JSON.stringify(selectedVersion.data[key])}
                              </Text>
                            </Paper>
                          )}

                          {diff.modified.includes(key) && (
                            <Stack gap={4}>
                              <Paper withBorder p="xs" style={{ backgroundColor: '#ffe0e0' }}>
                                <Text size="xs" c="red" fw={500}>
                                  - {JSON.stringify(selectedVersion.data[key])}
                                </Text>
                              </Paper>
                              <Paper withBorder p="xs" style={{ backgroundColor: '#e0ffe0' }}>
                                <Text size="xs" c="green" fw={500}>
                                  + {JSON.stringify(currentData[key])}
                                </Text>
                              </Paper>
                            </Stack>
                          )}

                          {diff.added.includes(key) && (
                            <Paper withBorder p="xs" style={{ backgroundColor: '#e0ffe0' }}>
                              <Text size="xs" c="green" fw={500}>
                                + {JSON.stringify(currentData[key])}
                              </Text>
                            </Paper>
                          )}
                        </div>
                      ))}

                      {diff.added.length === 0 &&
                        diff.modified.length === 0 &&
                        diff.removed.length === 0 && (
                          <Text size="sm" c="dimmed" ta="center" py="xl">
                            No differences found
                          </Text>
                        )}
                    </Stack>
                  </Paper>
                </Tabs.Panel>
              </Tabs>

              {/* Restore Button */}
              <Group justify="flex-end">
                <Button
                  leftSection={<IconArrowBackUp size={16} />}
                  color="orange"
                  onClick={() => handleRestore(selectedVersion.version)}
                >
                  Restore Version {selectedVersion.version}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      )}

      {/* Selected Version Details (if not in compare mode) */}
      {!compareMode && selectedVersion && (
        <Paper shadow="xs" p="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={5}>Version {selectedVersion.version} Data</Title>
              <Group>
                <Tooltip label="Compare with Current">
                  <ActionIcon
                    color="blue"
                    variant="light"
                    onClick={() => setCompareMode(true)}
                  >
                    <IconGitCompare size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Restore This Version">
                  <ActionIcon
                    color="orange"
                    variant="light"
                    onClick={() => handleRestore(selectedVersion.version)}
                  >
                    <IconRestore size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>

            <div>
              <Text size="xs" c="dimmed" mb="xs">
                Saved by {selectedVersion.savedBy} on {new Date(selectedVersion.savedAt).toLocaleString()}
              </Text>
              {selectedVersion.changeDescription && (
                <Alert icon={<IconInfoCircle size={16} />} color="blue" mb="xs">
                  <Text size="xs">{selectedVersion.changeDescription}</Text>
                </Alert>
              )}
            </div>

            <JsonInput
              value={JSON.stringify(selectedVersion.data, null, 2)}
              readOnly
              minRows={15}
              maxRows={25}
              autosize
            />
          </Stack>
        </Paper>
      )}

      {/* Actions */}
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onClose}>
          Close
        </Button>
      </Group>
    </Stack>
  );
}
